import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, MapPin, MoveRight } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import type { ApiStorageNode } from '@/types/storage'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { filterUnitsByCondition } from '@/lib/productStorage'
import { useCapacitySummary, useSetProductLocation, useStorageConditions } from '@/hooks/useStorage'
import {
  RequirementNotice,
  requiredConditionOf,
  useStorageRequirement,
} from '@/components/procedure/StorageAssignmentDialog'
import { LocationTrail } from './picker/LocationTrail'
import { StorageLocationPicker } from './picker/StorageLocationPicker'
import { KeptAtBadge } from './KeptAtBadge'
import { cn } from '@/lib/utils'

/**
 * Move one product to another free position (z8nrz7f7g4).
 *
 * The same operation as the lot's Set location, offered from Products →
 * Products: before this a placed product could only be re-filed from the
 * Storage map. It states where the product is now, offers only free positions
 * kept at what its catalog entry needs, and never offers the position it is
 * already in — a move that goes nowhere is not a move.
 *
 * Laid out as From → To (z8nrz7fqfy): the current address, the picker walked
 * the way someone walks to a freezer — building, room, unit, slot — and the
 * chosen slot's full address before anything is saved. Units that are out of
 * service are shown but cannot be chosen, and opening one costs a page of free
 * slots, not its whole subtree.
 *
 * The server stays the authority (one product per slot, never alongside stock,
 * condition match); this narrows the choices so a refusal is the exception.
 */
export function MoveProductDialog({
  product,
  onClose,
}: {
  product: CreatedProduct | null
  onClose: () => void
}) {
  const open = product !== null
  const conditions = useStorageConditions()
  // Only while open: the dialog is mounted wherever products are listed.
  const summary = useCapacitySummary(open)
  const setProductLocation = useSetProductLocation()
  const [target, setTarget] = useState<ApiStorageNode | null>(null)

  useEffect(() => {
    if (open) {
      setTarget(null)
      setProductLocation.reset()
    }
    // `setProductLocation` is a stable mutation object; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Read from the catalog entry rather than handed in: a missing or still
  // loading requirement would read as "any unit" and end in a 409 at Save.
  const requirement = useStorageRequirement(product ? [product.catalogEntryId] : [])
  const requiresCondition = requiredConditionOf(requirement)
  const blocked = requirement.status !== 'ready'
  const conditionMeta = useMemo(() => conditions.data?.conditions ?? [], [conditions.data])
  const currentPath = product?.storagePath ?? null

  const { eligible, hidden } = useMemo(
    () => (blocked ? { eligible: [], hidden: 0 } : filterUnitsByCondition(summary.data ?? [], requiresCondition)),
    [summary.data, requiresCondition, blocked],
  )
  const units = useMemo(() => eligible.filter((u) => u.remaining > 0), [eligible])
  // Open on the unit it is in, when that unit can take it, so a move within a
  // freezer does not start from the top. A path's first segment is its unit.
  const owningUnitId = useMemo(() => {
    const code = currentPath?.split('/')[0]
    return units.find((u) => u.code === code)?.nodeId ?? null
  }, [units, currentPath])

  function commit() {
    if (!product || !target) return
    setProductLocation.mutate(
      { productId: product.id, storageNodeId: target.id },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='gap-0 p-0 sm:max-w-3xl'>
        <DialogHeader className='space-y-1 border-b px-6 pb-4 pt-6'>
          <DialogTitle className='flex items-center gap-2'>
            <MoveRight className='h-5 w-5 text-primary' />
            Move product
          </DialogTitle>
          <DialogDescription className='flex flex-wrap items-center gap-2'>
            <span className='font-mono font-medium text-foreground'>{product?.din}</span>
            {product?.productDescriptionCode && <span className='font-mono'>{product.productDescriptionCode}</span>}
            {requiresCondition && <KeptAtBadge condition={requiresCondition} />}
          </DialogDescription>
        </DialogHeader>

        <div className='max-h-[70vh] space-y-4 overflow-y-auto px-6 py-4'>
          <div className='grid gap-2'>
            <AddressCard label='From'>
              {currentPath ? (
                <LocationTrail path={currentPath} />
              ) : (
                <p className='text-sm italic text-muted-foreground'>Not put away yet</p>
              )}
            </AddressCard>
            <div className='flex justify-center text-muted-foreground' aria-hidden>
              <ArrowDown className='h-4 w-4' />
            </div>
            <AddressCard label='To' active={target != null}>
              {target ? (
                <LocationTrail path={target.path} />
              ) : (
                <p className='text-sm text-muted-foreground'>Pick a building, a room, a storage unit, then a free slot below.</p>
              )}
            </AddressCard>
          </div>

          <RequirementNotice requirement={requirement} hidden={hidden} conditions={conditionMeta} />

          {!blocked &&
            (summary.isLoading ? (
              <p className='py-8 text-center text-sm text-muted-foreground'>Loading storage…</p>
            ) : requiresCondition && units.length === 0 ? (
              <p className='flex flex-wrap items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
                No unit kept at <KeptAtBadge condition={requiresCondition} /> has a free slot. Set a unit's Kept at in
                Storage first.
                <Link to='/inventory?tab=storage' className='font-medium text-primary hover:underline' onClick={onClose}>
                  Go to Storage
                </Link>
              </p>
            ) : (
              <StorageLocationPicker
                units={units}
                requires={requiresCondition}
                conditions={conditionMeta}
                initialUnitId={owningUnitId}
                selectedSlotId={target?.id ?? null}
                onSelectSlot={setTarget}
              />
            ))}

          {setProductLocation.error && (
            <p className='text-sm text-destructive'>{String(setProductLocation.error.message)}</p>
          )}
        </div>

        <div className='flex justify-end gap-2 border-t px-6 py-4'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!target || blocked || setProductLocation.isPending} onClick={commit}>
            <MapPin className='mr-1.5 h-4 w-4' />
            {setProductLocation.isPending ? 'Moving…' : 'Move here'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AddressCard({ label, active = false, children }: { label: string; active?: boolean; children: React.ReactNode }) {
  return (
    <div className={cn('rounded-xl border p-3', active ? 'border-primary/40 bg-primary/5' : 'bg-muted/30')}>
      <p className='mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>{label}</p>
      {children}
    </div>
  )
}
